import { useEffect, useState } from 'react';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import {
  Plus, Search, Receipt, Pause, Play, XCircle, Pencil, Repeat, Banknote, Users as UsersIcon, CalendarClock,
} from 'lucide-react';
import { Topbar } from '@/components/Topbar';
import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { Modal } from '@/components/Modal';
import { Input } from '@/components/Input';
import { Textarea } from '@/components/Textarea';
import { Select } from '@/components/Select';
import { FormField } from '@/components/FormField';
import { StatusPill } from '@/components/StatusPill';
import { EmptyState } from '@/components/EmptyState';
import { LoadErrorState, NoMatchesState } from '@/components/ListStates';
import { useLabels, useCompanyCurrency, useCurrencyField } from '@/contexts/WorkspaceContext';
import {
  retainersService, Retainer, RetainerBillingCycle, RetainerStatus, RetainersSummary,
} from '@/services/retainers.service';
import { contactsService } from '@/services/contacts.service';
import { projectsService } from '@/services/projects.service';
import { todayYmd } from '@/utils/date';

const STATUS_VARIANT: Record<RetainerStatus, 'success' | 'warning' | 'danger'> = {
  active: 'success', paused: 'warning', cancelled: 'danger',
};

const CYCLE_LABEL: Record<RetainerBillingCycle, string> = { monthly: '/mo', quarterly: '/qtr', yearly: '/yr' };

export default function RetainersPage() {
  const t = useLabels();
  const companyCurrency = useCompanyCurrency();
  const [summary, setSummary] = useState<RetainersSummary | null>(null);
  const [retainers, setRetainers] = useState<Retainer[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [q, setQ] = useState('');
  const [status, setStatus] = useState<RetainerStatus | ''>('');
  const [contacts, setContacts] = useState<Array<{ contactUid: string; firstName: string; lastName?: string }>>([]);
  const [projects, setProjects] = useState<Array<{ projectUid: string; name: string }>>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Retainer | null>(null);
  const [busy, setBusy] = useState(false);

  // Form state
  const [name, setName] = useState('');
  const [clientContactUid, setClientContactUid] = useState('');
  const [projectUid, setProjectUid] = useState('');
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useCurrencyField();
  const [billingCycle, setBillingCycle] = useState<RetainerBillingCycle>('monthly');
  const [startDate, setStartDate] = useState(todayYmd());
  const [includedHours, setIncludedHours] = useState('');
  const [notes, setNotes] = useState('');

  const contactName = (uid: string) => {
    const c = contacts.find((x) => x.contactUid === uid);
    return c ? [c.firstName, c.lastName].filter(Boolean).join(' ') : uid;
  };
  const projectName = (uid?: string) => (uid ? projects.find((p) => p.projectUid === uid)?.name ?? uid : null);

  const refresh = async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const [list, sum] = await Promise.all([
        retainersService.list({ q: q || undefined, status: status || undefined, limit: 100 }),
        retainersService.summary(),
      ]);
      setRetainers(list.items);
      setTotal(list.total);
      setSummary(sum);
    } catch { setLoadError(true); } finally { setLoading(false); }
  };
  const filtersActive = !!q.trim() || !!status;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { refresh(); }, [q, status]);

  useEffect(() => {
    contactsService.list({ limit: 200 }).then((r) => setContacts(r.items.map((c) => ({ contactUid: c.contactUid, firstName: c.firstName, lastName: c.lastName })))).catch(() => {});
    projectsService.list({ limit: 200 }).then((r) => setProjects(r.items.map((p) => ({ projectUid: p.projectUid, name: p.name })))).catch(() => {});
  }, []);

  const openNewModal = () => {
    setEditing(null);
    setName(''); setClientContactUid(''); setProjectUid(''); setAmount('');
    setCurrency(companyCurrency); setBillingCycle('monthly'); setStartDate(todayYmd());
    setIncludedHours(''); setNotes('');
    setModalOpen(true);
  };

  const openEditModal = (r: Retainer) => {
    setEditing(r);
    setName(r.name); setClientContactUid(r.clientContactUid); setProjectUid(r.projectUid ?? ''); setAmount(String(r.amount));
    setCurrency(r.currency); setBillingCycle(r.billingCycle); setStartDate(r.startDate.slice(0, 10));
    setIncludedHours(r.includedHours != null ? String(r.includedHours) : ''); setNotes(r.notes ?? '');
    setModalOpen(true);
  };

  const save = async () => {
    if (!name.trim() || !clientContactUid || !amount) { toast.error('Name, client and amount are required'); return; }
    setBusy(true);
    try {
      const payload = {
        name: name.trim(),
        clientContactUid,
        projectUid: projectUid || undefined,
        amount: parseFloat(amount),
        currency,
        billingCycle,
        startDate,
        includedHours: includedHours ? parseFloat(includedHours) : undefined,
        notes: notes.trim() || undefined,
      };
      if (editing) {
        await retainersService.update(editing.retainerUid, payload);
        toast.success(`${t('retainers', 'singular')} updated`);
      } else {
        await retainersService.create(payload);
        toast.success(`${t('retainers', 'singular')} created`);
      }
      setModalOpen(false);
      refresh();
    } catch { /* interceptor toasts */ } finally { setBusy(false); }
  };

  const invoiceNow = async (r: Retainer) => {
    // eslint-disable-next-line no-restricted-globals, no-alert
    if (!window.confirm(`Invoice ${r.name} for ${r.amount.toLocaleString()} ${r.currency} and advance to the next period?`)) return;
    try {
      await retainersService.invoicePeriod(r.retainerUid);
      toast.success('Invoiced — next billing date advanced');
      refresh();
    } catch { /* interceptor toasts */ }
  };

  const togglePause = async (r: Retainer) => {
    try {
      if (r.status === 'active') await retainersService.pause(r.retainerUid);
      else await retainersService.resume(r.retainerUid);
      toast.success(r.status === 'active' ? 'Retainer paused' : 'Retainer resumed');
      refresh();
    } catch { /* interceptor toasts */ }
  };

  const cancel = async (r: Retainer) => {
    // eslint-disable-next-line no-restricted-globals, no-alert
    if (!window.confirm(`Cancel ${r.name}? This can't be undone.`)) return;
    try {
      await retainersService.cancel(r.retainerUid);
      toast.success('Retainer cancelled');
      refresh();
    } catch { /* interceptor toasts */ }
  };

  const isOverdue = (r: Retainer) => r.status === 'active' && new Date(r.nextBillingDate).getTime() < Date.now();

  return (
    <>
      <Topbar
        title={t('retainers')}
        breadcrumb={`${t('operations')} / ${t('retainers')}`}
        description={loading ? 'Loading…' : `${total} ${total === 1 ? t('retainers', 'singular').toLowerCase() : t('retainers').toLowerCase()}`}
        right={<Button variant="primary" iconLeft={<Plus size={14} />} onClick={openNewModal}>New {t('retainers', 'singular').toLowerCase()}</Button>}
      />

      <div className="w-full px-4 py-6 sm:px-7 sm:py-7 space-y-5">
        {/* KPI tiles */}
        <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">
          <KpiTile label="Monthly recurring revenue" value={summary ? `${summary.mrr.toLocaleString(undefined, { maximumFractionDigits: 0 })} ${summary.currency}` : '—'} icon={<Banknote size={14} />} />
          <KpiTile label="Active retainers" value={summary ? String(summary.activeCount) : '—'} icon={<Repeat size={14} />} />
          <KpiTile label="Due this week" value={summary ? String(summary.dueThisWeek) : '—'} icon={<CalendarClock size={14} />} warn={!!summary && summary.dueThisWeek > 0} />
          <KpiTile
            label="Hours used vs included"
            value={summary && summary.includedHours > 0 ? `${summary.hoursUsed.toFixed(0)} / ${summary.includedHours.toFixed(0)}h` : '—'}
            icon={<UsersIcon size={14} />}
          />
        </div>
        {summary && Object.keys(summary.otherCurrencies).length > 0 && (
          <p className="text-xs text-text-muted">
            Also recurring: {Object.entries(summary.otherCurrencies).map(([cur, amt]) => `${amt.toLocaleString(undefined, { maximumFractionDigits: 0 })} ${cur}/mo`).join(', ')}
          </p>
        )}

        {/* Filters */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="relative w-72">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
            <input
              className="w-full rounded-md border border-border bg-surface py-2 pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              value={q} onChange={(e) => setQ(e.target.value)} placeholder={`Search ${t('retainers').toLowerCase()}…`}
            />
          </div>
          <div className="flex flex-wrap gap-1.5">
            {(['', 'active', 'paused', 'cancelled'] as const).map((s) => (
              <button
                key={s}
                onClick={() => setStatus(s)}
                className={clsx(
                  'rounded-full border px-3 py-1.5 text-xs font-medium capitalize transition-colors',
                  status === s ? 'border-primary bg-primary-soft text-primary' : 'border-border bg-surface text-text-secondary hover:bg-surface-alt',
                )}
              >
                {s || 'All'}
              </button>
            ))}
          </div>
        </div>

        {loading && <div className="skeleton h-64 rounded-2xl" />}

        {!loading && loadError && (
          <LoadErrorState what={`your ${t('retainers').toLowerCase()}`} onRetry={() => void refresh()} />
        )}

        {!loading && !loadError && retainers.length === 0 && filtersActive && (
          <NoMatchesState onClear={() => { setQ(''); setStatus(''); }} />
        )}

        {!loading && !loadError && retainers.length === 0 && !filtersActive && (
          <EmptyState
            icon={<Repeat size={28} />}
            title={`No ${t('retainers').toLowerCase()} yet`}
            description={`Set up a recurring engagement with a ${t('contacts', 'singular').toLowerCase()} to track billing and hours automatically.`}
            action={<Button onClick={openNewModal} iconLeft={<Plus size={14} />}>New {t('retainers', 'singular').toLowerCase()}</Button>}
          />
        )}

        {!loading && !loadError && retainers.length > 0 && (
          <Card className="!p-0 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-2xs uppercase tracking-wide text-text-muted">
                  <th className="px-5 py-2.5 font-medium">Name</th>
                  <th className="px-2 py-2.5 font-medium">{t('contacts', 'singular')}</th>
                  <th className="px-2 py-2.5 font-medium">Amount</th>
                  <th className="px-2 py-2.5 font-medium">Next billing</th>
                  <th className="px-2 py-2.5 font-medium">Hours</th>
                  <th className="px-2 py-2.5 font-medium">Status</th>
                  <th className="px-5 py-2.5 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {retainers.map((r) => {
                  const overdue = isOverdue(r);
                  const hoursPct = r.includedHours ? Math.min(100, (r.hoursUsed / r.includedHours) * 100) : null;
                  return (
                    <tr key={r.retainerUid} className="hover:bg-surface-alt/50">
                      <td className="px-5 py-3">
                        <div className="font-medium text-text-primary">{r.name}</div>
                        {r.projectUid && <div className="text-2xs text-text-muted">{projectName(r.projectUid)}</div>}
                      </td>
                      <td className="px-2 py-3 text-text-secondary">{contactName(r.clientContactUid)}</td>
                      <td className="px-2 py-3 whitespace-nowrap tabular-nums text-text-primary">
                        {r.amount.toLocaleString()} {r.currency}<span className="text-text-muted">{CYCLE_LABEL[r.billingCycle]}</span>
                      </td>
                      <td className={clsx('px-2 py-3 whitespace-nowrap tabular-nums', overdue ? 'font-semibold text-danger' : 'text-text-secondary')}>
                        {new Date(r.nextBillingDate).toLocaleDateString()} {overdue && '· overdue'}
                      </td>
                      <td className="px-2 py-3 w-32">
                        {hoursPct !== null ? (
                          <div>
                            <div className="mb-1 text-2xs text-text-muted">{r.hoursUsed.toFixed(0)}/{r.includedHours}h</div>
                            <div className="h-1.5 w-full overflow-hidden rounded-full bg-surface-alt">
                              <div className={clsx('h-full rounded-full', hoursPct >= 100 ? 'bg-danger' : 'bg-primary')} style={{ width: `${hoursPct}%` }} />
                            </div>
                          </div>
                        ) : <span className="text-text-muted">—</span>}
                      </td>
                      <td className="px-2 py-3"><StatusPill status={r.status} variant={STATUS_VARIANT[r.status]} /></td>
                      <td className="px-5 py-3">
                        <div className="flex items-center justify-end gap-1">
                          {r.status === 'active' && (
                            <button onClick={() => invoiceNow(r)} title="Invoice now" className="flex h-7 w-7 items-center justify-center rounded-lg text-text-muted hover:bg-surface-alt hover:text-primary">
                              <Receipt size={14} />
                            </button>
                          )}
                          {r.status !== 'cancelled' && (
                            <button onClick={() => togglePause(r)} title={r.status === 'active' ? 'Pause' : 'Resume'} className="flex h-7 w-7 items-center justify-center rounded-lg text-text-muted hover:bg-surface-alt hover:text-warning">
                              {r.status === 'active' ? <Pause size={14} /> : <Play size={14} />}
                            </button>
                          )}
                          <button onClick={() => openEditModal(r)} title="Edit" className="flex h-7 w-7 items-center justify-center rounded-lg text-text-muted hover:bg-surface-alt hover:text-text-primary">
                            <Pencil size={14} />
                          </button>
                          {r.status !== 'cancelled' && (
                            <button onClick={() => cancel(r)} title="Cancel" className="flex h-7 w-7 items-center justify-center rounded-lg text-text-muted hover:bg-surface-alt hover:text-danger">
                              <XCircle size={14} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </Card>
        )}
      </div>

      <Modal open={modalOpen} onClose={() => !busy && setModalOpen(false)} title={editing ? `Edit ${t('retainers', 'singular').toLowerCase()}` : `New ${t('retainers', 'singular').toLowerCase()}`} widthClass="max-w-xl">
        <FormField label="Name" required>
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Monthly support retainer" autoFocus />
        </FormField>
        <FormField label={t('contacts', 'singular')} required hint={contacts.length ? undefined : `No ${t('contacts').toLowerCase()} yet`}>
          <Select value={clientContactUid} onChange={(e) => setClientContactUid(e.target.value)}>
            <option value="">— select —</option>
            {contacts.map((c) => <option key={c.contactUid} value={c.contactUid}>{[c.firstName, c.lastName].filter(Boolean).join(' ')}</option>)}
          </Select>
        </FormField>
        <FormField label={`Linked ${t('projects', 'singular').toLowerCase()}`} hint="Optional — enables hours-used tracking">
          <Select value={projectUid} onChange={(e) => setProjectUid(e.target.value)}>
            <option value="">— none —</option>
            {projects.map((p) => <option key={p.projectUid} value={p.projectUid}>{p.name}</option>)}
          </Select>
        </FormField>
        <div className="grid grid-cols-3 gap-3">
          <FormField label="Amount" required>
            <Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0.00" />
          </FormField>
          <FormField label="Currency">
            <Input value={currency} onChange={(e) => setCurrency(e.target.value.toUpperCase())} maxLength={4} />
          </FormField>
          <FormField label="Cycle">
            <Select value={billingCycle} onChange={(e) => setBillingCycle(e.target.value as RetainerBillingCycle)}>
              <option value="monthly">Monthly</option>
              <option value="quarterly">Quarterly</option>
              <option value="yearly">Yearly</option>
            </Select>
          </FormField>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <FormField label="Start date" required>
            <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} disabled={!!editing} />
          </FormField>
          <FormField label="Included hours" hint="Optional">
            <Input type="number" value={includedHours} onChange={(e) => setIncludedHours(e.target.value)} placeholder="e.g. 20" />
          </FormField>
        </div>
        <FormField label="Notes">
          <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </FormField>
        <div className="mt-2 flex justify-end gap-2 border-t border-border pt-4">
          <Button variant="secondary" onClick={() => setModalOpen(false)} disabled={busy}>Cancel</Button>
          <Button variant="primary" onClick={save} loading={busy}>{editing ? 'Save' : 'Create'}</Button>
        </div>
      </Modal>
    </>
  );
}

function KpiTile({ label, value, icon, warn }: { label: string; value: string; icon: React.ReactNode; warn?: boolean }) {
  return (
    <div className="rounded-2xl border border-border bg-surface p-4 shadow-soft">
      <div className="flex items-center gap-2 text-2xs font-medium uppercase tracking-wide text-text-muted">
        <span className={clsx('flex h-6 w-6 items-center justify-center rounded-lg', warn ? 'bg-danger-soft text-danger' : 'bg-primary-soft text-primary')}>{icon}</span>
        {label}
      </div>
      <p className={clsx('mt-2 font-display text-xl font-bold tabular-nums', warn ? 'text-danger' : 'text-text-primary')}>{value}</p>
    </div>
  );
}
